import React, { useState } from "react";
import {
  Grid,
  Card,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Button,
  FormControl,
  Autocomplete,
  TextField,
  Stack,
} from "@mui/material";
import Loader from "../../../Loader/loader";
import { useFormik } from "formik";
import * as Yup from "yup";
import Checkbox from "@mui/material/Checkbox";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { SetBlastIVR, getOutboundCdrRequest, getCallerIDRequest } from "../../../api/api";

function IVRBlastForm() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [trunkOption, settrunkOption] = React.useState([]);
  const [callerIdOptions, setCallerIdOptions] = useState([]);
  const validationSchema = Yup.object({
    trunk_name: Yup.string().required("Trunk is required"),
    number: Yup.string().required("Number is required"),
    caller_id: Yup.array()
      .min(1, "At least one Caller ID is required")
      .required("Caller ID is required"),

    count: Yup.string().required("Number of Calls is required"),
  });

  const formik = useFormik({
    initialValues: {
      trunk_name: "",
      number: "",
      caller_id: [],
      count: "",
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);

        await SetBlastIVR({
          trunk_name: values.trunk_name,
          number: values.number,
          caller_id: values.caller_id.join(","),
          count: values.count,
        });

        resetForm();
      } catch (error) {
        console.error("Password change error:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  React.useEffect(() => {
    const fetchtrunkOption = async () => {
      try {
        const res = await getOutboundCdrRequest();
        settrunkOption(res.data.data); // Make sure your API returns `data.data`
      } catch (error) {
        console.error("Error fetching trunk_id options:", error);
      }
    };

    fetchtrunkOption();
  }, []);

  React.useEffect(() => {
    const fetchCallerIds = async () => {
      try {
        const res = await getCallerIDRequest();

        setCallerIdOptions(
          res?.data?.data?.map((item) => ({
            label: item.caller_id,
            value: item.caller_id,
          })) || []
        );
      } catch (error) {
        console.error("Error fetching caller ids:", error);
      }
    };

    fetchCallerIds();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={4}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={6} lg={5}>
            <Card sx={{ padding: 3 }}>
              <MDTypography variant="h5" fontWeight="bold" mb={2}>
                IVR Blast
              </MDTypography>

              {isSubmitting ? (
                <Grid container justifyContent="center" alignItems="center" sx={{ py: 5 }}>
                  <Loader />
                </Grid>
              ) : (
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="trunk_id">Trunk *</InputLabel>
                        <FormControl
                          fullWidth
                          error={formik.touched.trunk_id && Boolean(formik.errors.trunk_id)}
                        >
                          <Autocomplete
                            options={trunkOption}
                            getOptionLabel={(option) => option.name || ""}
                            value={
                              trunkOption.find((opt) => opt.name === formik.values.trunk_name) ||
                              null
                            }
                            onChange={(_, newValue) => {
                              formik.setFieldValue("trunk_name", newValue ? newValue.name : "");
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select Trunk"
                                error={formik.touched.trunk_id && Boolean(formik.errors.trunk_id)}
                                helperText={formik.touched.trunk_id && formik.errors.trunk_id}
                              />
                            )}
                          />
                        </FormControl>
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="number">Number *</InputLabel>
                        <OutlinedInput
                          type="password"
                          id="number"
                          name="number"
                          fullWidth
                          placeholder="Enter Number "
                          value={formik.values.number}
                          onChange={formik.handleChange}
                          error={formik.touched.number && Boolean(formik.errors.number)}
                        />
                        {formik.touched.number && formik.errors.number && (
                          <FormHelperText error>{formik.errors.number}</FormHelperText>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="caller_id">Caller ID *</InputLabel>

                        <Autocomplete
                          multiple
                          options={callerIdOptions}
                          disableCloseOnSelect
                          getOptionLabel={(option) => option.label}
                          value={callerIdOptions.filter((option) =>
                            formik.values.caller_id.includes(option.value)
                          )}
                          onChange={(_, newValue) => {
                            formik.setFieldValue(
                              "caller_id",
                              newValue.map((item) => item.value)
                            );
                          }}
                          renderOption={(props, option, { selected }) => (
                            <li {...props}>
                              <Checkbox checked={selected} sx={{ mr: 1 }} />
                              {option.label}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Caller IDs"
                              error={formik.touched.caller_id && Boolean(formik.errors.caller_id)}
                              helperText={formik.touched.caller_id && formik.errors.caller_id}
                            />
                          )}
                        />
                      </Stack>
                    </Grid>

                    <Grid item xs={12}>
                      <Stack spacing={1}>
                        <InputLabel htmlFor="count">Number of Calls *</InputLabel>
                        <OutlinedInput
                          type="text"
                          id="count"
                          name="count"
                          fullWidth
                          placeholder="Enter Number of Calls"
                          value={formik.values.count}
                          onChange={formik.handleChange}
                          error={formik.touched.count && Boolean(formik.errors.count)}
                        />
                        {formik.touched.count && formik.errors.count && (
                          <FormHelperText error>{formik.errors.count}</FormHelperText>
                        )}
                      </Stack>
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                          backgroundColor: "#1A73E8",
                          color: "#FFFFFF",
                          fontWeight: "bold",
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "#1669C1",
                          },
                        }}
                      >
                        Submit
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default IVRBlastForm;
